// Regras puras de soltura do quadro do PCP.
// O cliente só antecipa o gesto: o servidor revalida processo, célula, D-13 e versão.
import type { KanbanCartao, KanbanColuna, KanbanComandoResultado, KanbanQuadro, KanbanRaia } from '@pieltec/contracts';

export interface EventoSoltura {
  readonly origem: { readonly raiaId: string; readonly indice: number };
  readonly destino: { readonly raiaId: string; readonly indice: number };
}

export type DecisaoSoltura =
  | { readonly tipo: 'reordenar'; readonly osId: string; readonly novaPosicao: number }
  | { readonly tipo: 'reordenar_fila_entrada'; readonly opId: string; readonly novaPosicao: number }
  | {
      readonly tipo: 'mover';
      readonly osId: string;
      readonly celulaDestino: string;
      readonly novaPosicao: number;
      readonly exigeConfirmacao: boolean;
    }
  | { readonly tipo: 'nenhum' }
  | { readonly tipo: 'recusado'; readonly motivo: string };

export interface LocalCartao {
  readonly coluna: KanbanColuna;
  readonly raia: KanbanRaia;
  readonly raiaId: string;
  readonly indice: number;
  readonly cartao: KanbanCartao;
}

const PREFIXO_RAIA = 'raia:';

export const MOTIVO_OUTRO_PROCESSO = 'Só é possível mover para outra célula do mesmo processo.';
export const MOTIVO_EM_PRODUCAO = 'Este cartão está em produção. Pause antes de mover para outra célula.';

export function idDropRaia(raiaId: string): string {
  return `${PREFIXO_RAIA}${raiaId}`;
}

export function localizarCartao(quadro: KanbanQuadro, chave: string): LocalCartao | null {
  for (const coluna of quadro.colunas) {
    for (const raia of coluna.raias) {
      const indice = raia.cartoes.findIndex((cartao) => cartao.chave === chave);
      const cartao = indice >= 0 ? raia.cartoes[indice] : undefined;
      if (cartao) {
        return { coluna, raia, raiaId: raia.id, indice, cartao };
      }
    }
  }
  return null;
}

export function localizarRaia(
  quadro: KanbanQuadro,
  raiaId: string,
): { readonly coluna: KanbanColuna; readonly raia: KanbanRaia } | null {
  for (const coluna of quadro.colunas) {
    const raia = coluna.raias.find((item) => item.id === raiaId);
    if (raia) return { coluna, raia };
  }
  return null;
}

/** Traduz o id do alvo do dnd-kit (cartão ou área da raia) em raia + índice. */
export function alvoSoltura(
  quadro: KanbanQuadro,
  alvoId: string,
): { readonly raiaId: string; readonly indice: number } | null {
  if (alvoId.startsWith(PREFIXO_RAIA)) {
    const raiaId = alvoId.slice(PREFIXO_RAIA.length);
    const achou = localizarRaia(quadro, raiaId);
    if (!achou) return null;
    return { raiaId, indice: achou.raia.cartoes.length };
  }
  const cartao = localizarCartao(quadro, alvoId);
  if (!cartao) return null;
  return { raiaId: cartao.raiaId, indice: cartao.indice };
}

export function resolverSoltura(evento: EventoSoltura, quadro: KanbanQuadro): DecisaoSoltura {
  const origem = localizarRaia(quadro, evento.origem.raiaId);
  const cartao = origem?.raia.cartoes[evento.origem.indice];
  if (!origem || !cartao) return { tipo: 'nenhum' };

  if (evento.origem.raiaId === evento.destino.raiaId) {
    if (evento.origem.indice === evento.destino.indice) return { tipo: 'nenhum' };
    // A API numera a posição a partir de 1, já com o cartão removido da lista.
    if (!cartao.osId) {
      if (origem.coluna.tipo === 'FILA_ENTRADA') {
        return { tipo: 'reordenar_fila_entrada', opId: cartao.opId, novaPosicao: evento.destino.indice + 1 };
      }
      return { tipo: 'nenhum' };
    }
    return { tipo: 'reordenar', osId: cartao.osId, novaPosicao: evento.destino.indice + 1 };
  }

  if (!cartao.osId) return { tipo: 'nenhum' };

  const destino = localizarRaia(quadro, evento.destino.raiaId);
  const mesmoProcesso =
    destino !== null &&
    origem.coluna.tipo === 'PROCESSO' &&
    destino.coluna.tipo === 'PROCESSO' &&
    origem.coluna.servicoId !== null &&
    origem.coluna.servicoId === destino.coluna.servicoId &&
    destino.raia.tipo === 'CELULA' &&
    destino.raia.ativa &&
    destino.raia.celulaCodigo !== null;

  if (!destino || !mesmoProcesso || !destino.raia.celulaCodigo) {
    return { tipo: 'recusado', motivo: MOTIVO_OUTRO_PROCESSO };
  }

  if (cartao.estado === 'EM_PRODUCAO') {
    return { tipo: 'recusado', motivo: MOTIVO_EM_PRODUCAO };
  }

  return {
    tipo: 'mover',
    osId: cartao.osId,
    celulaDestino: destino.raia.celulaCodigo,
    novaPosicao: evento.destino.indice + 1,
    exigeConfirmacao: cartao.estado === 'PAUSADO',
  };
}

export const MOTIVO_CARTAO_MUDOU = 'O cartão mudou enquanto a confirmação estava aberta. Confira o quadro e tente de novo.';
export const MOTIVO_RAIA_MUDOU = 'A célula de destino mudou enquanto a confirmação estava aberta. Confira o quadro e tente de novo.';

export type MovimentoConfirmado =
  | {
      readonly tipo: 'mover';
      readonly cartao: KanbanCartao;
      readonly raiaDestinoId: string;
      readonly celulaDestino: string;
      readonly novaPosicao: number;
    }
  | { readonly tipo: 'abortar'; readonly motivo: string };

/**
 * Reavalia um movimento que aguardava confirmação contra o quadro VIGENTE (WR-WEB-04): o cartão
 * (com a versão atual), a raia e a posição podem ter mudado por SSE enquanto o diálogo estava aberto.
 */
export function reavaliarMovimentoConfirmado(
  quadro: KanbanQuadro,
  alvo: { readonly chave: string; readonly raiaDestinoId: string; readonly indiceDestino: number },
): MovimentoConfirmado {
  const origem = localizarCartao(quadro, alvo.chave);
  if (!origem?.cartao.osId) return { tipo: 'abortar', motivo: MOTIVO_CARTAO_MUDOU };
  const destino = localizarRaia(quadro, alvo.raiaDestinoId);
  if (!destino) return { tipo: 'abortar', motivo: MOTIVO_RAIA_MUDOU };

  const decisao = resolverSoltura(
    {
      origem: { raiaId: origem.raiaId, indice: origem.indice },
      destino: { raiaId: alvo.raiaDestinoId, indice: Math.min(alvo.indiceDestino, destino.raia.cartoes.length) },
    },
    quadro,
  );
  if (decisao.tipo === 'recusado') return { tipo: 'abortar', motivo: decisao.motivo };
  if (decisao.tipo !== 'mover') return { tipo: 'abortar', motivo: MOTIVO_CARTAO_MUDOU };
  return {
    tipo: 'mover',
    cartao: origem.cartao,
    raiaDestinoId: alvo.raiaDestinoId,
    celulaDestino: decisao.celulaDestino,
    novaPosicao: decisao.novaPosicao,
  };
}

function listaNaPosicao(
  cartoes: readonly KanbanCartao[],
  chave: string,
  novaPosicao: number,
): KanbanCartao[] {
  const indice = cartoes.findIndex((cartao) => cartao.chave === chave);
  const cartao = indice >= 0 ? cartoes[indice] : undefined;
  if (!cartao) return cartoes.map((item, posicao) => ({ ...item, posicao: posicao + 1 }));
  const sem = cartoes.filter((item) => item.chave !== chave);
  const destino = Math.max(0, Math.min(novaPosicao - 1, sem.length));
  const proxima = [...sem.slice(0, destino), cartao, ...sem.slice(destino)];
  return proxima.map((item, posicao) => ({ ...item, posicao: posicao + 1 }));
}

/** Devolve um quadro novo com o cartão na posição 1-based, sem alterar o original. */
export function aplicarReordenacaoOtimista(
  quadro: KanbanQuadro,
  chave: string,
  raiaId: string,
  novaPosicao: number,
): KanbanQuadro {
  return {
    ...quadro,
    colunas: quadro.colunas.map((coluna) => ({
      ...coluna,
      raias: coluna.raias.map((raia) =>
        raia.id === raiaId
          ? { ...raia, cartoes: listaNaPosicao(raia.cartoes, chave, novaPosicao) }
          : raia,
      ),
    })),
  };
}

/** Move o cartão para outra raia sem alterar o quadro original. */
export function aplicarMovimentoOtimista(
  quadro: KanbanQuadro,
  osId: string,
  raiaDestinoId: string,
  novaPosicao: number,
): KanbanQuadro {
  let movido: KanbanCartao | undefined;
  for (const coluna of quadro.colunas) {
    for (const raia of coluna.raias) {
      const achou = raia.cartoes.find((cartao) => cartao.osId === osId);
      if (achou) movido = achou;
    }
  }
  if (!movido) return quadro;
  const destinoRaia = localizarRaia(quadro, raiaDestinoId)?.raia;
  const cartaoMovido: KanbanCartao = {
    ...movido,
    celulaCodigo: destinoRaia?.celulaCodigo ?? movido.celulaCodigo,
  };

  return {
    ...quadro,
    colunas: quadro.colunas.map((coluna) => ({
      ...coluna,
      raias: coluna.raias.map((raia) => {
        const sem = raia.cartoes.filter((cartao) => cartao.osId !== osId);
        if (raia.id !== raiaDestinoId) {
          return sem.length === raia.cartoes.length ? raia : { ...raia, cartoes: sem };
        }
        return { ...raia, cartoes: listaNaPosicao([...sem, cartaoMovido], osId, novaPosicao) };
      }),
    })),
  };
}

export function raiaCompativel(cartao: KanbanCartao, celulaCodigo: string): boolean {
  return cartao.cabinesSugeridas?.includes(celulaCodigo) ?? false;
}

/** A raia inteira é um alvo válido (reordenar na mesma, ou mover/alocar). */
export function raiaAceitaCartao(quadro: KanbanQuadro, chave: string, raiaDestinoId: string): boolean {
  const origem = localizarCartao(quadro, chave);
  if (!origem?.cartao.osId) return false;
  if (origem.raiaId === raiaDestinoId) return true;
  const decisao = resolverSoltura(
    {
      origem: { raiaId: origem.raiaId, indice: origem.indice },
      destino: { raiaId: raiaDestinoId, indice: 0 },
    },
    quadro,
  );
  return decisao.tipo === 'mover';
}

export interface DestinoMover {
  readonly celulaCodigo: string;
  readonly titulo: string;
  readonly compativel: boolean;
}

/**
 * Células ativas do mesmo processo. Em produção não há destino — o menu pede pausa.
 * Em "A alocar" o cartão ainda não está em célula nenhuma (a `celulaCodigo` dele é a
 * célula prevista no roteiro, com `aguardandoAlocacao`), então todas as cabines ativas
 * são destino, inclusive a do roteiro. Numa célula, a própria célula não é destino.
 */
export function destinosMoverPara(quadro: KanbanQuadro, cartao: KanbanCartao): DestinoMover[] {
  if (cartao.estado === 'EM_PRODUCAO') return [];
  const local = localizarCartao(quadro, cartao.chave);
  if (!local || local.coluna.tipo !== 'PROCESSO' || local.coluna.servicoId === null) return [];

  const emAlocar = local.raia.tipo === 'A_ALOCAR';
  const destinos: DestinoMover[] = [];
  for (const raia of local.coluna.raias) {
    if (raia.tipo !== 'CELULA' || !raia.ativa || !raia.celulaCodigo) continue;
    if (!emAlocar && raia.celulaCodigo === cartao.celulaCodigo) continue;
    destinos.push({
      celulaCodigo: raia.celulaCodigo,
      titulo: raia.titulo,
      compativel: emAlocar && raiaCompativel(cartao, raia.celulaCodigo),
    });
  }

  destinos.sort((a, b) => {
    if (emAlocar && a.compativel !== b.compativel) return a.compativel ? -1 : 1;
    return a.celulaCodigo.localeCompare(b.celulaCodigo, 'pt-BR');
  });
  return destinos;
}

/** Grava a versão devolvida pelo comando para o próximo gesto não nascer em 409. */
export function aplicarVersaoComando(quadro: KanbanQuadro, resultado: KanbanComandoResultado): KanbanQuadro {
  return {
    ...quadro,
    colunas: quadro.colunas.map((coluna) => ({
      ...coluna,
      raias: coluna.raias.map((raia) => ({
        ...raia,
        cartoes: raia.cartoes.map((cartao) =>
          cartao.osId === resultado.osId
            ? {
                ...cartao,
                versao: resultado.versao,
                posicao: resultado.posicao,
                celulaCodigo: resultado.celulaCodigo,
              }
            : cartao,
        ),
      })),
    })),
  };
}
